import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import NotificationBell from '../NotificationBell';
import { OnboardingTour } from '../OnboardingTour';
import { ProgressIndicator } from '../ProgressIndicator';
import GlobalSearch from '../GlobalSearch';
import {
  LayoutDashboard, Settings, LogOut, Menu, X, ChevronDown,
  Shield, FileSearch, CheckSquare, Building2, CreditCard, FileText,
  Search, HelpCircle, MessageCircle, Users, Zap, AlertTriangle, ShieldAlert, Grid3X3
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../ui/tooltip';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel,
} from '../ui/dropdown-menu';

const SPOOKER_LOGO = "https://spooker.io/wp-content/uploads/2022/12/logo.svg";

/* ─── Announcement Bar ─── */
const ANNOUNCEMENT_VERSION = 'v2.0';
const ANNOUNCEMENT_STORAGE_KEY = `spk_announcement_${ANNOUNCEMENT_VERSION}_dismissed`;

const AnnouncementBar = ({ onDismiss, hidden }) => {
  const [visible, setVisible] = useState(() => {
    try { return localStorage.getItem(ANNOUNCEMENT_STORAGE_KEY) !== 'true'; }
    catch { return true; }
  });
  if (!visible || hidden) return null;
  return (
    <div className="bg-[#1B1F3B] text-white text-xs flex items-center justify-center gap-3 px-4 py-2 relative z-50" data-testid="announcement-bar">
      <Zap className="h-3.5 w-3.5 text-amber-400 shrink-0" strokeWidth={2} />
      <span className="text-neutral-200">Nieuw: beoordeel betrouwbaarheid per indicator en zie direct waar je staat.</span>
      <Link to="/beoordelen/volwassenheid" className="font-semibold text-blue-400 hover:text-blue-300 underline underline-offset-2">Bekijk volwassenheid</Link>
      <button
        onClick={(e) => { e.stopPropagation(); setVisible(false); localStorage.setItem(ANNOUNCEMENT_STORAGE_KEY, 'true'); if (onDismiss) onDismiss(); }}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-white transition-colors p-1 z-10"
        data-testid="announcement-dismiss"
        aria-label="Sluiten"
      >
        <X className="h-3.5 w-3.5 pointer-events-none" strokeWidth={2} />
      </button>
    </div>
  );
};

/* ─── Global Header (dark top bar) ─── */
const GlobalHeader = ({ user, onLogout, onSearchOpen, navigate }) => {
  return (
    <header className="bg-[#111827] h-11 flex items-center justify-between px-4 z-40 shrink-0" data-testid="global-header">
      {/* Left: brand + product */}
      <div className="flex items-center gap-3">
        <Link to="/dashboard" className="flex items-center gap-2">
          <img src={SPOOKER_LOGO} alt="Spooker" className="h-4 invert opacity-90" />
        </Link>
        <div className="h-4 w-px bg-white/20" />
        {/* Product Switcher */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-1.5 text-xs font-semibold text-white/90 hover:text-white transition-colors" data-testid="product-switcher">
              <Shield className="h-3.5 w-3.5 text-blue-400" strokeWidth={2} />
              Security Platform
              <ChevronDown className="h-3 w-3 text-white/50" strokeWidth={2} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64 bg-[#1F2937] border-white/10 text-white">
            <DropdownMenuLabel className="text-[10px] text-neutral-400 uppercase tracking-wider">Producten</DropdownMenuLabel>
            <DropdownMenuItem className="text-xs cursor-pointer focus:bg-white/10 focus:text-white" onClick={() => navigate('/dashboard')}>
              <Shield className="h-3.5 w-3.5 mr-2 text-blue-400" strokeWidth={1.5} />Security Overview
            </DropdownMenuItem>
            <DropdownMenuItem className="text-xs cursor-pointer focus:bg-white/10 focus:text-white" onClick={() => navigate('/beoordelen/nulmeting')}>
              <FileSearch className="h-3.5 w-3.5 mr-2 text-emerald-400" strokeWidth={1.5} />Gap Analyse
            </DropdownMenuItem>
            <DropdownMenuItem className="text-xs cursor-pointer focus:bg-white/10 focus:text-white" onClick={() => navigate('/risicos/overzicht')}>
              <AlertTriangle className="h-3.5 w-3.5 mr-2 text-amber-400" strokeWidth={1.5} />Risicobeheer
            </DropdownMenuItem>
            <DropdownMenuItem className="text-xs cursor-pointer focus:bg-white/10 focus:text-white" onClick={() => navigate('/verbeteren/maatregelen')}>
              <CheckSquare className="h-3.5 w-3.5 mr-2 text-violet-400" strokeWidth={1.5} />Maatregelen
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem className="text-xs cursor-pointer focus:bg-white/10 focus:text-white" onClick={() => navigate('/subscription')}>
              <Grid3X3 className="h-3.5 w-3.5 mr-2 text-neutral-400" strokeWidth={1.5} />Alle modules
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Right: global actions */}
      <div className="flex items-center gap-1">
        <button
          onClick={onSearchOpen}
          className="h-7 px-2.5 flex items-center gap-1.5 text-[11px] text-white/50 hover:text-white/80 bg-white/5 hover:bg-white/10 rounded-md transition-colors"
          data-testid="global-search-trigger"
        >
          <Search className="h-3 w-3" strokeWidth={2} />
          <span className="hidden sm:inline">Zoeken...</span>
          <kbd className="hidden sm:inline text-[9px] text-white/30 bg-white/10 rounded px-1 py-0.5 ml-2 font-mono">⌘K</kbd>
        </button>
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button onClick={() => navigate('/contacts')} className="h-7 w-7 flex items-center justify-center text-white/50 hover:text-white/80 hover:bg-white/10 rounded-md transition-colors" data-testid="global-invite-team">
                <Users className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-[10px]">Team uitnodigen</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <NotificationBell />
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button onClick={() => navigate('/settings')} className="h-7 w-7 flex items-center justify-center text-white/50 hover:text-white/80 hover:bg-white/10 rounded-md transition-colors" data-testid="global-help">
                <HelpCircle className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-[10px]">Help &amp; Documentatie</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {/* User avatar */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="h-7 w-7 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center ml-1 hover:ring-2 hover:ring-blue-400/50 transition-all" data-testid="user-menu-button">
              {(user?.full_name || user?.email || '?').charAt(0).toUpperCase()}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-3 py-2.5">
              <p className="text-sm font-medium text-[#111827] truncate">{user?.full_name || 'Gebruiker'}</p>
              <p className="text-xs text-[#6B7280] truncate">{user?.email}</p>
              {user?.organization_name && <p className="text-xs text-[#6B7280] truncate mt-0.5">{user.organization_name}</p>}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Account</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => navigate('/settings')} className="text-sm cursor-pointer" data-testid="menu-settings">
              <Settings className="h-4 w-4 mr-2" strokeWidth={1.5} />Profiel & instellingen
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/subscription')} className="text-sm cursor-pointer" data-testid="menu-subscription">
              <CreditCard className="h-4 w-4 mr-2" strokeWidth={1.5} />Abonnement
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/invoices')} className="text-sm cursor-pointer" data-testid="menu-invoices">
              <FileText className="h-4 w-4 mr-2" strokeWidth={1.5} />Facturen
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout} data-testid="logout-button" className="text-sm text-[#B91C1C] cursor-pointer focus:text-[#B91C1C]">
              <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} />Uitloggen
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

/* ─── Sidebar — 5 hoofditems ─── */
const mainNavigation = [
  { name: 'Home',        href: '/dashboard',    icon: LayoutDashboard, tip: 'Dashboard, prioriteiten en voortgang' },
  { name: 'Beoordelen',  href: '/beoordelen',   icon: FileSearch,      tip: 'Nulmeting en volwassenheidsbeoordeling', match: ['/beoordelen', '/assessment', '/maturity'] },
  { name: "Risico's",    href: '/risicos',      icon: Shield,          tip: "Risico's, kwetsbaarheden, dreigingen en systemen", match: ['/risicos', '/risks', '/vulnerabilities', '/threats', '/assets'] },
  { name: 'Verbeteren',  href: '/verbeteren',   icon: CheckSquare,     tip: 'Maatregelen, strategie, financieel en leveranciers', match: ['/verbeteren', '/measures', '/strategy', '/financial', '/leveranciers'] },
  { name: 'Organisatie', href: '/organisatie',   icon: Building2,       tip: 'Bedrijfsgegevens, mensen, teams en verantwoordelijkheden', match: ['/organisatie', '/organization', '/contacts', '/teams', '/raci'] },
];

const MainLayout = ({ children }) => {
  const { user, logout, api } = useAuth();
  const { planName } = useSubscription() || {};
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [mfaStatus, setMfaStatus] = useState({ enabled: true, graceDays: null });
  const [showMfaBanner, setShowMfaBanner] = useState(false);
  const sidebarRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [announcementVisible, setAnnouncementVisible] = useState(true);

  useEffect(() => {
    const checkMfaStatus = async () => {
      try {
        const res = await api.get('/user/mfa/status');
        const enabled = res.data.mfa_enabled;
        const graceDays = res.data.grace_days_remaining;
        setMfaStatus({ enabled, graceDays });
        if (!enabled && graceDays !== null && graceDays >= 0) {
          const dismissed = localStorage.getItem('mfa_dismissed');
          if (dismissed === 'true') {
            setShowMfaBanner(false);
          } else {
            const snoozedUntil = localStorage.getItem('mfa_snoozed_until');
            if (snoozedUntil && new Date(snoozedUntil) > new Date()) {
              setShowMfaBanner(false);
            } else {
              localStorage.removeItem('mfa_snoozed_until');
              setShowMfaBanner(true);
            }
          }
        }
      } catch {}
    };
    if (user) checkMfaStatus();
  }, [user, api]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const isActive = (item) => {
    if (item.href === '/dashboard') return location.pathname === '/dashboard';
    if (item.match) return item.match.some(m => location.pathname.startsWith(m));
    return location.pathname.startsWith(item.href);
  };

  const handleSidebarEnter = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setSidebarExpanded(true);
  };
  const handleSidebarLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => { setSidebarExpanded(false); }, 200);
  };
  useEffect(() => { return () => { if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current); }; }, []);

  // Tour sidebar events
  useEffect(() => {
    const expand = () => { if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current); setSidebarExpanded(true); };
    const collapse = () => { setSidebarExpanded(false); };
    window.addEventListener('tour-expand-sidebar', expand);
    window.addEventListener('tour-collapse-sidebar', collapse);
    return () => { window.removeEventListener('tour-expand-sidebar', expand); window.removeEventListener('tour-collapse-sidebar', collapse); };
  }, []);

  // Cmd+K
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setSearchOpen(prev => !prev); }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const collapsed = !sidebarExpanded;
  const sidebarWidth = collapsed ? 'w-[52px]' : 'w-56';
  const contentMargin = 'lg:ml-[52px]';

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const active = isActive(item);

    return (
      <TooltipProvider key={item.name} delayDuration={collapsed ? 100 : 600}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to={item.href}
              className={`relative flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-2.5 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                active
                  ? 'bg-[#111827] text-white'
                  : 'text-[#4B5563] hover:bg-[#F3F4F6] hover:text-[#111827]'
              }`}
              onClick={() => setSidebarOpen(false)}
              data-testid={`nav-${item.name.toLowerCase().replace(/'/g, '').replace(/ /g, '-')}`}
            >
              <Icon className={`h-5 w-5 shrink-0 ${active ? 'text-white' : ''}`} strokeWidth={active ? 2 : 1.5} />
              {!collapsed && <span className="truncate">{item.name}</span>}
            </Link>
          </TooltipTrigger>
          {collapsed && (
            <TooltipContent side="right" className="text-xs font-medium">
              {item.name}
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    );
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col" data-testid="app-shell">
      {/* Announcement Bar — verberg als MFA-banner zichtbaar is */}
      <AnnouncementBar onDismiss={() => setAnnouncementVisible(false)} hidden={showMfaBanner && !mfaStatus.enabled} />

      {/* Global Dark Header */}
      <GlobalHeader user={user} onLogout={handleLogout} onSearchOpen={() => setSearchOpen(true)} navigate={navigate} />

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/20 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Content area with sidebar */}
      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <aside
          ref={sidebarRef}
          onMouseEnter={handleSidebarEnter}
          onMouseLeave={handleSidebarLeave}
          className={`fixed top-0 left-0 z-50 ${sidebarWidth} bg-white border-r border-neutral-200 transition-all duration-200 ease-out h-screen
            ${sidebarOpen ? 'translate-x-0 !w-56' : '-translate-x-full'} lg:translate-x-0 lg:sticky lg:top-0 lg:h-screen`}
          data-testid="main-sidebar"
        >
          <div className="flex flex-col h-full">
            {/* Sidebar Header */}
            <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'} h-12 px-3 border-b border-[#E5E7EB] shrink-0`}>
              {collapsed ? (
                <div className="h-6 w-6 rounded-md bg-[#111827] flex items-center justify-center">
                  <span className="text-[10px] font-black text-white">S</span>
                </div>
              ) : (
                <div className="flex items-center gap-2.5">
                  <div className="h-6 w-6 rounded-md bg-[#111827] flex items-center justify-center">
                    <span className="text-[10px] font-black text-white">S</span>
                  </div>
                  <span className="text-sm font-bold text-[#111827]">Spooker</span>
                </div>
              )}
              <button className="lg:hidden p-1 hover:bg-[#F3F4F6] rounded" onClick={() => setSidebarOpen(false)}>
                <X className="h-4 w-4 text-[#6B7280]" strokeWidth={1.5} />
              </button>
            </div>

            {/* Navigation — 5 hoofditems */}
            <nav className="flex-1 px-2.5 py-3 flex flex-col" data-testid="sidebar-nav">
              <div className="space-y-1">
                {mainNavigation.map(item => renderNavItem(item))}
              </div>

              {/* Admin */}
              {user?.role === 'admin' && (
                <div className="mt-auto pt-4">
                  {collapsed && <div className="h-px bg-[#E5E7EB] mx-1.5 mb-2" />}
                  {renderNavItem({ name: 'Admin', href: '/admin', icon: Shield })}
                </div>
              )}
            </nav>

            {/* Help */}
            <div className="px-2.5 pb-1">
              <TooltipProvider delayDuration={collapsed ? 100 : 600}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to="/settings"
                      className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-2.5 py-2 rounded-lg text-sm font-medium text-[#6B7280] hover:bg-[#F3F4F6] hover:text-[#111827] transition-colors`}
                      data-testid="nav-hulp"
                    >
                      <HelpCircle className="h-5 w-5 shrink-0" strokeWidth={1.5} />
                      {!collapsed && <span>Hulp</span>}
                    </Link>
                  </TooltipTrigger>
                  {collapsed && <TooltipContent side="right" className="text-xs font-medium">Hulp</TooltipContent>}
                </Tooltip>
              </TooltipProvider>
            </div>

            {/* User section */}
            <div className="border-t border-[#E5E7EB] px-2.5 py-2.5">
              <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-2.5'}`}>
                <div className="h-8 w-8 rounded-full bg-[#111827] text-white text-xs font-bold flex items-center justify-center shrink-0">
                  {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                {!collapsed && (
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#111827] truncate">{user?.full_name}</p>
                    <p className="text-xs text-[#6B7280] truncate">{user?.organization_name}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <div className={`flex-1 min-w-0 flex flex-col ${contentMargin}`}>
          {/* Mobile Header */}
          <header className="sticky top-0 z-30 flex items-center justify-between h-11 px-4 bg-white border-b border-neutral-200 lg:hidden">
            <div className="flex items-center gap-2">
              <button className="p-1.5 -ml-1 hover:bg-neutral-100 rounded-md" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-button">
                <Menu className="h-5 w-5 text-neutral-600" strokeWidth={1.5} />
              </button>
              <span className="text-xs font-semibold text-neutral-800">Security Platform</span>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7 text-neutral-500" onClick={() => setSearchOpen(true)} data-testid="header-search-mobile">
                <Search className="h-3.5 w-3.5" strokeWidth={1.5} />
              </Button>
              <NotificationBell />
            </div>
          </header>

          {/* MFA Warning */}
          {showMfaBanner && !mfaStatus.enabled && (
            <div className="mx-4 mt-2 flex items-center justify-between gap-3 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200" data-testid="mfa-warning-banner">
              <div className="flex items-center gap-2 min-w-0">
                <ShieldAlert className="h-3.5 w-3.5 text-amber-500 shrink-0" strokeWidth={1.5} />
                <span className="text-[11px] text-amber-700 truncate">
                  {mfaStatus.graceDays === 0 ? "MFA is vereist voor uw account" : `MFA instellen, nog ${mfaStatus.graceDays} dagen`}
                </span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button onClick={() => navigate('/settings?setup_mfa=required')} className="px-2.5 py-1 rounded-md bg-amber-500 text-white text-[10px] font-semibold hover:bg-amber-600 transition-colors" data-testid="mfa-setup-button">Instellen</button>
                {mfaStatus.graceDays > 0 && (
                  <button onClick={() => { localStorage.setItem('mfa_snoozed_until', new Date(Date.now() + 7 * 86400000).toISOString()); setShowMfaBanner(false); }} className="px-2 py-1 rounded-md text-amber-600 text-[10px] hover:bg-amber-100 transition-colors" data-testid="mfa-snooze-button">Later</button>
                )}
                <button onClick={() => { localStorage.setItem('mfa_dismissed', 'true'); setShowMfaBanner(false); }} className="p-0.5 hover:bg-amber-100 rounded text-amber-400" data-testid="mfa-dismiss-button">
                  <X className="h-3 w-3" />
                </button>
              </div>
            </div>
          )}

          {/* Page Content */}
          <main className="flex-1 animate-fade-in">
            {children}
          </main>
        </div>
      </div>

      {/* Onboarding Tour */}
      <OnboardingTour />

      {/* Global Search */}
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />

      {/* Floating Support CTA */}
      <button
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 bg-[#111827] text-white text-xs font-medium px-4 py-2.5 rounded-full shadow-lg hover:bg-neutral-800 transition-all hover:scale-105"
        onClick={() => navigate('/settings')}
        data-testid="floating-support-cta"
      >
        <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
        <span className="hidden sm:inline">Hulp nodig?</span>
      </button>
    </div>
  );
};

export default MainLayout;
